import { AlertRuleVariant } from "modals/AlertRuleEditModal"
import { AlertRuleDraft } from "modals/AlertRuleEditModal/types"

import { AlertRuleConfiguration, AlertRuleId, AlertRuleParameters } from "./alertRules.types"

export interface AlertRuleCatalogEntry {
  id: AlertRuleId
  variant: AlertRuleVariant
  title: string
}

export const alertRuleCatalog: AlertRuleCatalogEntry[] = [
  {
    id: "cooling_potential_issue",
    variant: AlertRuleVariant.CoolingEfficiency,
    title: "System efficiency for cooling",
  },
  {
    id: "heating_potential_issue",
    variant: AlertRuleVariant.HeatingEfficiency,
    title: "System efficiency for heating",
  },
  { id: "temp_high", variant: AlertRuleVariant.HighTemperature, title: "High temperature detected" },
  { id: "temp_low", variant: AlertRuleVariant.LowTemperature, title: "Low temperature detected" },
  { id: "humidity_high", variant: AlertRuleVariant.HighHumidity, title: "High humidity detected" },
  { id: "humidity_low", variant: AlertRuleVariant.LowHumidity, title: "Low humidity detected" },
  { id: "tvoc_high", variant: AlertRuleVariant.AirQuality, title: "Air quality TVOC reading" },
]

export const alertRuleCatalogById = Object.fromEntries(alertRuleCatalog.map(item => [item.id, item])) as Record<
  AlertRuleId,
  AlertRuleCatalogEntry
>

export const alertRuleCatalogByVariant = Object.fromEntries(
  alertRuleCatalog.map(item => [item.variant, item]),
) as Record<AlertRuleVariant, AlertRuleCatalogEntry>

export const getAlertRuleVariant = (alertRuleId: AlertRuleId) => alertRuleCatalogById[alertRuleId].variant

export const getAlertRuleTitle = (alertRuleId: AlertRuleId) => alertRuleCatalogById[alertRuleId].title

const getParameterValue = (parameters: AlertRuleParameters | null | undefined, key: string, fallback: string) =>
  parameters?.[key] != null ? String(parameters[key]) : fallback

const formatAlertRuleNumber = (value: number) => {
  return String(Math.round(value))
}

const celsiusToFahrenheit = (celsius: number) => formatAlertRuleNumber(celsius * 1.8 + 32)

const fahrenheitToCelsius = (fahrenheit: number) => Number((((fahrenheit - 32) * 5) / 9).toFixed(1))

const getTemperatureThresholdValue = (parameters: AlertRuleParameters | null | undefined, fallbackCelsius: number) =>
  celsiusToFahrenheit(Number(getParameterValue(parameters, "temperature_threshold_celsius", String(fallbackCelsius))))

const getTemperatureThresholdCelsius = (draft: AlertRuleDraft) =>
  draft.thresholdUnit === "C" ? Number(draft.thresholdValue) : fahrenheitToCelsius(Number(draft.thresholdValue))

export const getInitialDraftFromRule = (
  alertRuleId: AlertRuleId,
  parameters?: AlertRuleParameters | null,
): AlertRuleDraft => {
  switch (alertRuleId) {
    case "cooling_potential_issue":
    case "heating_potential_issue":
      return {
        thresholdValue: getParameterValue(parameters, "evaluation_window_hours", "4"),
        durationValue: getParameterValue(parameters, "evaluation_window_hours", "4"),
        durationUnit: "hour",
      }
    case "temp_high":
    case "temp_low":
      return {
        thresholdValue: getTemperatureThresholdValue(parameters, alertRuleId === "temp_high" ? 37.8 : 4.4),
        thresholdUnit: "F",
        durationValue: getParameterValue(parameters, "evaluation_window_minutes", "15"),
        durationUnit: "min",
      }
    case "humidity_high":
    case "humidity_low":
      return {
        thresholdValue: getParameterValue(
          parameters,
          "humidity_threshold_percent",
          alertRuleId === "humidity_high" ? "80" : "20",
        ),
        thresholdUnit: "%",
        durationValue: getParameterValue(parameters, "evaluation_window_minutes", "15"),
        durationUnit: "min",
      }
    case "tvoc_high":
      return {
        thresholdValue: getParameterValue(parameters, "tvoc_threshold", "10"),
        durationValue: getParameterValue(parameters, "evaluation_window_minutes", "15"),
        durationUnit: "min",
      }
  }
}

export const buildParametersFromDraft = (alertRuleId: AlertRuleId, draft: AlertRuleDraft): AlertRuleParameters => {
  switch (alertRuleId) {
    case "cooling_potential_issue":
    case "heating_potential_issue":
      return {
        evaluation_window_hours: Number(draft.durationValue),
      }
    case "temp_high":
    case "temp_low":
      return {
        evaluation_window_minutes: Number(draft.durationValue),
        temperature_threshold_celsius: getTemperatureThresholdCelsius(draft),
      }
    case "humidity_high":
    case "humidity_low":
      return {
        evaluation_window_minutes: Number(draft.durationValue),
        humidity_threshold_percent: Number(draft.thresholdValue),
      }
    case "tvoc_high":
      return {
        evaluation_window_minutes: Number(draft.durationValue),
        tvoc_threshold: Number(draft.thresholdValue),
      }
  }
}

export const getRuleName = (rule: Pick<AlertRuleConfiguration, "alert_rule_id" | "alert_name">) =>
  rule.alert_name || getAlertRuleTitle(rule.alert_rule_id)
